import type {
  CreateRoomRequest,
  CreateRoomResponse,
  CreateSuccessorRequest,
  CreateSuccessorResponse,
  JoinRoomRequest,
  JoinRoomResponse,
} from '../shared/protocol.js';
import type {
  MapDefinition,
  PlayerIdentity,
  PublicLobbySummary,
  PublicRoom,
  VisualTuning,
} from '../shared/types.js';
import { isWeather } from '../shared/types.js';
import { CAR_BY_ID } from '../shared/cars.js';
import type { DailyChallenge } from '../shared/gameplay-fun.js';
import { PROTOCOL_HEADER, PROTOCOL_VERSION } from '../shared/protocol-version.js';
import { parsePublicRoom } from '../shared/public-room-validation.js';
import {
  createMapCache,
  LEGACY_MAP_CACHE_KEY,
  MAP_CACHE_KEY,
  parseMapCache,
  validateMapSet,
} from '../shared/map-cache.js';

export const API_REQUEST_TIMEOUT_MS = 15_000;
export const METADATA_REQUEST_TIMEOUT_MS = 5_000;
export const PUBLISHED_DAILY_CACHE_KEY = 'nightshift:daily-published:v1';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code = '') {
    super(message);
    this.name = 'ApiError';
  }
}

// Race control answers an unknown or expired room with INVALID_INVITE (409)
// so room ids cannot be probed; for a stored pass that means the race is gone.
const UNAVAILABLE_RACE_CODES = new Set(['INVALID_INVITE', 'ROOM_NOT_FOUND', 'UNAUTHORISED']);

/** True when a stored reconnect pass can never resume its room again. */
export function isUnavailableRaceError(error: unknown): boolean {
  return error instanceof ApiError
    && ([401, 403, 404, 410].includes(error.status) || UNAVAILABLE_RACE_CODES.has(error.code));
}

export class ProtocolVersionError extends Error {
  constructor() {
    super('A newer NIGHTSHIFT build is required.');
    this.name = 'ProtocolVersionError';
  }
}

export class ApiTimeoutError extends Error {
  constructor() {
    super('Race control did not respond in time.');
    this.name = 'ApiTimeoutError';
  }
}

export async function getMaps(): Promise<MapDefinition[]> {
  try {
    const response = await request<unknown>('/api/maps', {}, METADATA_REQUEST_TIMEOUT_MS);
    const maps = validateMapSet(response);
    if (!maps) throw new ApiError('Race control returned an invalid map set.', 502, 'INVALID_MAPS');
    try {
      localStorage.setItem(MAP_CACHE_KEY, JSON.stringify(createMapCache(maps)));
      localStorage.removeItem(LEGACY_MAP_CACHE_KEY);
    } catch {
      // Cache is optional.
    }
    return maps;
  } catch (error) {
    // A cached map is useful during a genuine outage, but it must never hide
    // an explicit wire-version/schema mismatch or a valid HTTP client error.
    // Those responses prove that race control answered and must be surfaced.
    if (
      error instanceof ProtocolVersionError
      || error instanceof ApiError && (error.code === 'INVALID_MAPS' || error.status < 500)
    ) throw error;
    const maps = loadCachedMaps();
    if (maps) return maps;
    throw error;
  }
}

export async function getDailyChallenge(): Promise<DailyChallenge | null> {
  try {
    const response = await request<unknown>('/api/daily', {}, METADATA_REQUEST_TIMEOUT_MS);
    const challenge = parseDailyChallenge(response);
    if (!challenge || challenge.id !== new Date().toISOString().slice(0, 10)) {
      throw new ApiError('Race control returned a stale or invalid Daily challenge.', 502, 'INVALID_DAILY');
    }
    try {
      localStorage.setItem(PUBLISHED_DAILY_CACHE_KEY, JSON.stringify(challenge));
    } catch {
      // Cache is optional.
    }
    return challenge;
  } catch (error) {
    if (
      error instanceof ProtocolVersionError
      || error instanceof ApiError && (error.code === 'INVALID_DAILY' || error.status < 500)
    ) throw error;
    const cached = loadCachedDailyChallenge();
    if (cached) return cached;
    throw error;
  }
}

/** Synchronous, validated startup path. Network revalidation is deliberately
 * separate so a half-open mobile radio cannot hold a cached garage hostage. */
export function loadCachedMaps(): MapDefinition[] | null {
  try {
    const cached = JSON.parse(localStorage.getItem(MAP_CACHE_KEY) ?? 'null') as unknown;
    const maps = parseMapCache(cached);
    if (maps) return maps;
  } catch {
    // Corrupt JSON must not poison every later offline launch.
  }
  clearMapCache();
  return null;
}

export function loadCachedDailyChallenge(date = new Date()): DailyChallenge | null {
  try {
    const cached = parseDailyChallenge(JSON.parse(
      localStorage.getItem(PUBLISHED_DAILY_CACHE_KEY) ?? 'null',
    ) as unknown);
    if (cached?.id === date.toISOString().slice(0, 10)) return cached;
    localStorage.removeItem(PUBLISHED_DAILY_CACHE_KEY);
  } catch {
    try {
      localStorage.removeItem(PUBLISHED_DAILY_CACHE_KEY);
    } catch {
      // LocalStorage may be unavailable.
    }
  }
  return null;
}

function clearMapCache(): void {
  try {
    localStorage.removeItem(MAP_CACHE_KEY);
    localStorage.removeItem(LEGACY_MAP_CACHE_KEY);
  } catch {
    // LocalStorage is optional and can be blocked by browser policy.
  }
}

export async function createRoom(payload: CreateRoomRequest): Promise<CreateRoomResponse> {
  const response = await request<unknown>('/api/rooms', { method: 'POST', body: JSON.stringify(payload) });
  const parsed = parseCreateRoomResponse(response);
  if (!parsed) throw invalidResponse('room creation');
  return parsed;
}

export async function getRoom(roomId: string, inviteToken: string): Promise<PublicRoom> {
  const response = await request<unknown>(`/api/rooms/${encodeURIComponent(roomId)}?invite=${encodeURIComponent(inviteToken)}`);
  const parsed = parsePublicRoom(response);
  if (!parsed) throw invalidResponse('room lookup');
  return parsed;
}

export async function joinRoom(roomId: string, payload: JoinRoomRequest): Promise<JoinRoomResponse> {
  const response = await request<unknown>(`/api/rooms/${encodeURIComponent(roomId)}/join`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const parsed = parseJoinRoomResponse(response);
  if (!parsed) throw invalidResponse('room join');
  return parsed;
}

/** Public lobbies listed by race control; rows carry no credentials. */
export async function getPublicLobbies(signal?: AbortSignal): Promise<PublicLobbySummary[]> {
  const response = await request<unknown>('/api/lobbies', { signal }, METADATA_REQUEST_TIMEOUT_MS);
  if (!isRecord(response) || !Array.isArray(response.lobbies)) throw invalidResponse('lobby list');
  return response.lobbies.map(parseLobbySummary).filter((lobby): lobby is PublicLobbySummary => lobby !== null);
}

export async function createSuccessorRoom(
  roomId: string,
  payload: CreateSuccessorRequest,
): Promise<CreateSuccessorResponse> {
  const response = await request<unknown>(`/api/rooms/${encodeURIComponent(roomId)}/successor`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!isRecord(response)) throw invalidResponse('party transfer');
  const room = parsePublicRoom(response.room);
  if (!room || !validToken(response.inviteToken)) throw invalidResponse('party transfer');
  return { room, inviteToken: response.inviteToken };
}

async function request<T>(
  url: string,
  init: RequestInit = {},
  timeoutMs = API_REQUEST_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  const callerSignal = init.signal;
  let timedOut = false;
  const abortFromCaller = (): void => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) abortFromCaller();
  else callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      ...init,
      signal: controller.signal,
      headers: { 'content-type': 'application/json', ...init.headers },
    });
    const rawServerVersion = response.headers.get(PROTOCOL_HEADER);
    const serverVersion = rawServerVersion === null ? Number.NaN : Number(rawServerVersion);
    if (
      response.ok && rawServerVersion === null
      || rawServerVersion !== null
        && (!Number.isInteger(serverVersion) || serverVersion !== PROTOCOL_VERSION)
    ) {
      throw new ProtocolVersionError();
    }
    const body = (await response.json().catch(() => ({}))) as { error?: string; code?: string } & T;
    if (!response.ok) throw new ApiError(body.error ?? `Request failed (${response.status})`, response.status, body.code);
    return body;
  } catch (error) {
    if (timedOut) throw new ApiTimeoutError();
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
    callerSignal?.removeEventListener('abort', abortFromCaller);
  }
}

function parseCreateRoomResponse(value: unknown): CreateRoomResponse | null {
  if (!isRecord(value)) return null;
  const room = parsePublicRoom(value.room);
  const player = parsePlayerIdentity(value.player);
  if (!room || !player || room.hostPlayerId !== player.id || !validToken(value.inviteToken)) return null;
  return { room, player, inviteToken: value.inviteToken };
}

function parseJoinRoomResponse(value: unknown): JoinRoomResponse | null {
  if (!isRecord(value)) return null;
  const room = parsePublicRoom(value.room);
  const player = parsePlayerIdentity(value.player);
  if (!room || !player || room.members && !room.members.some((member) => member.id === player.id)) return null;
  return validToken(value.inviteToken) ? { room, player, inviteToken: value.inviteToken } : { room, player };
}

function parseLobbySummary(value: unknown): PublicLobbySummary | null {
  if (
    !isRecord(value)
    || !validIdentifier(value.id)
    || typeof value.hostName !== 'string'
    || value.hostName.length > 24
    || !validIdentifier(value.mapId)
    || typeof value.eventMode !== 'string'
    || !['lobby', 'countdown', 'racing', 'finished'].includes(String(value.phase))
    || !Number.isInteger(value.playerCount)
    || !Number.isInteger(value.maxPlayers)
    || !Number.isInteger(value.npcCount)
    || !['dawn', 'day', 'sunset', 'night'].includes(String(value.timeOfDay))
    || !isWeather(value.weather)
    || typeof value.createdAt !== 'number'
  ) return null;
  return value as unknown as PublicLobbySummary;
}

function parsePlayerIdentity(value: unknown): PlayerIdentity | null {
  if (
    !isRecord(value)
    || !validIdentifier(value.id)
    || !validToken(value.token)
    || !validText(value.name, 24)
    || !validIdentifier(value.carId)
    || !CAR_BY_ID.has(value.carId)
    || !validVisualTuning(value.tuning)
    || (value.crewModule !== undefined && !['balanced', 'grip', 'nitro', 'armour'].includes(String(value.crewModule)))
    || (value.signatureActive !== undefined && typeof value.signatureActive !== 'boolean')
  ) return null;
  return value as unknown as PlayerIdentity;
}

function parseDailyChallenge(value: unknown): DailyChallenge | null {
  if (
    !isRecord(value)
    || typeof value.id !== 'string'
    || !validUtcDateId(value.id)
    || !validIdentifier(value.mapId)
    || !validIdentifier(value.carId)
    || !CAR_BY_ID.has(value.carId)
    || !isWeather(value.weather)
    || !['dawn', 'day', 'sunset', 'night'].includes(String(value.timeOfDay))
  ) return null;
  return value as unknown as DailyChallenge;
}

function validVisualTuning(value: unknown): value is VisualTuning {
  if (!isRecord(value)) return false;
  return ['paint', 'accent', 'rims', 'neon'].every((key) => (
    typeof value[key] === 'string' && /^#[0-9a-f]{6}$/i.test(value[key] as string)
  ))
    && typeof value.neonEnabled === 'boolean'
    && ['none', 'ducktail', 'gt'].includes(String(value.spoiler))
    && ['turbine', 'mesh', 'five-spoke'].includes(String(value.wheels))
    && ['none', 'center-stripe', 'twin-stripe', 'chevron', 'circuit'].includes(String(value.decal))
    && finite(value.stance)
    && Number(value.stance) >= -0.08
    && Number(value.stance) <= 0.08;
}

function validIdentifier(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function validUtcDateId(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function validToken(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{8,200}$/.test(value);
}

function validText(value: unknown, maximumLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maximumLength;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function invalidResponse(operation: string): ApiError {
  return new ApiError(`Race control returned invalid data for ${operation}.`, 502, 'INVALID_RESPONSE');
}
